import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Skill, Team } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { SkillPermissionsTab } from "@/features/skills/components/SkillPermissionsTab";
import { SkillVersionsTab } from "@/features/skills/components/SkillVersionsTab";
import {
  useCreateSkill,
  useUpdateSkill,
} from "@/features/skills/hooks/useSkills";
import { useTeams } from "@/features/teams/hooks/useTeams";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill?: Skill | null;
}

export function SkillFormDialog({ open, onOpenChange, skill }: Props) {
  const { t } = useTranslation("common");
  const { t: ts } = useTranslation("skills");
  const isEdit = Boolean(skill);
  const create = useCreateSkill();
  const update = useUpdateSkill(skill?.id ?? "");
  const { data: teams } = useTeams({ include_inactive: false });
  const { isAdmin, memberships } = useAuth();

  const eligibleTeams = useMemo<Team[]>(() => {
    if (!teams) return [];
    if (isAdmin) return teams;
    const ids = new Set(
      memberships
        .filter((m) => m.role === "manager" || m.role === "creator")
        .map((m) => m.team.id),
    );
    return teams.filter((tm) => ids.has(tm.id));
  }, [teams, isAdmin, memberships]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [ownerTeamId, setOwnerTeamId] = useState("");

  useEffect(() => {
    if (open && skill) {
      setName(skill.name);
      setDescription(skill.description);
      setInstructions(skill.instructions);
      setOwnerTeamId(skill.owner_team_id);
    } else if (open) {
      setName("");
      setDescription("");
      setInstructions("");
      setOwnerTeamId(eligibleTeams[0]?.id ?? "");
    }
  }, [open, skill, eligibleTeams]);

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    try {
      if (isEdit && skill) {
        await update.mutateAsync({ name, description, instructions });
        toast.success(ts("form.toasts.updated"));
      } else {
        if (!ownerTeamId) {
          toast.error(ts("form.toasts.selectOwnerTeam"));
          return;
        }
        await create.mutateAsync({
          name,
          description,
          instructions,
          owner_team_id: ownerTeamId,
        });
        toast.success(ts("form.toasts.created"));
      }
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  const pending = create.isPending || update.isPending;
  const valid =
    name.trim().length > 0 &&
    description.trim().length > 0 &&
    instructions.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? ts("form.titleEdit") : ts("form.titleNew")}
          </DialogTitle>
          <DialogDescription>{ts("form.description")}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="content">
          <TabsList>
            <TabsTrigger value="content">{ts("form.tabs.content")}</TabsTrigger>
            {isEdit ? (
              <TabsTrigger value="permissions">
                {ts("form.tabs.permissions")}
              </TabsTrigger>
            ) : null}
            {isEdit ? (
              <TabsTrigger value="versions">
                {ts("form.tabs.versions")}
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="content">
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div className="space-y-1">
                <Label htmlFor="skill-name">{ts("form.labels.name")}</Label>
                <Input
                  id="skill-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="pesquisa-web"
                />
                <p className="text-xs text-muted-foreground">
                  {ts("form.labels.nameHint")}
                </p>
              </div>
              {!isEdit ? (
                <div className="space-y-1">
                  <Label htmlFor="skill-owner">
                    {ts("form.labels.ownerTeam")}
                  </Label>
                  {eligibleTeams.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {ts("form.labels.noEligibleTeam")}
                    </p>
                  ) : (
                    <select
                      id="skill-owner"
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      value={ownerTeamId}
                      onChange={(e) => setOwnerTeamId(e.target.value)}
                      required
                    >
                      <option value="" disabled>
                        {ts("form.labels.select")}
                      </option>
                      {eligibleTeams.map((tm) => (
                        <option key={tm.id} value={tm.id}>
                          {tm.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : null}
              <div className="space-y-1">
                <Label htmlFor="skill-desc">
                  {ts("form.labels.description")}
                </Label>
                <Textarea
                  id="skill-desc"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {ts("form.labels.descriptionHint")}
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="skill-instructions">
                  {ts("form.labels.instructions")}
                </Label>
                <Textarea
                  id="skill-instructions"
                  rows={10}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-xs text-muted-foreground">
                  {ts("form.labels.instructionsHint")}
                </p>
              </div>
            </form>
          </TabsContent>

          {isEdit && skill ? (
            <TabsContent value="permissions">
              <SkillPermissionsTab skill={skill} />
            </TabsContent>
          ) : null}

          {isEdit && skill ? (
            <TabsContent value="versions">
              <SkillVersionsTab skill={skill} />
            </TabsContent>
          ) : null}
        </Tabs>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("actions.close")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pending || !valid}
          >
            {isEdit ? t("actions.save") : t("actions.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
